import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Upload, Save, LogOut } from "lucide-react";
import Navbar, { UserAvatar } from "../components/Navbar";
import Footer from "../components/Footer";
import { api, formatError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ChangePasswordForm } from "../components/ChangePasswordForm";

const DISCORD_URL = "https://discord.gg/nEvwBUtqAV";

export default function SettingsPage() {
  const { user, loading, setUser, logout } = useAuth();
  const [username, setUsername] = useState(user?.username || user?.name || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  React.useEffect(() => {
    if (user) setUsername(user.username || user.name || "");
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading...</div>;
  if (!user) return <Navigate to="/" replace />;

  const saveUsername = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/me", { username });
      setUser(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
    setSaving(false);
  };

  const uploadAvatar = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: updated } = await api.patch("/me", { avatar_url: data.url });
      setUser(updated);
      toast.success("Avatar updated");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
    setUploading(false);
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Toaster position="top-right" theme="dark" toastOptions={{ className: "glass-dark !border-red-500/30" }} />
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono">Account</div>
          <h1 className="font-display font-black text-5xl tracking-tighter mt-2">Settings</h1>
        </div>

        <section className="glass-dark rounded-2xl p-6 md:p-8 mb-6">
          <h2 className="font-display font-bold text-xl mb-5">Profile</h2>
          <div className="flex items-start gap-6 mb-6">
            <UserAvatar user={user} size={80} />
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">Avatar</div>
              <label className="btn-ghost-glass !py-2 !px-4 !text-xs cursor-pointer inline-flex">
                <Upload size={12} /> {uploading ? "Uploading..." : "Change photo"}
                <input
                  type="file" accept="image/*" className="hidden"
                  disabled={uploading}
                  data-testid="settings-avatar-upload"
                  onChange={(e) => e.target.files?.[0] && uploadAvatar(e.target.files[0])}
                />
              </label>
              <div className="text-xs text-zinc-500 mt-2">PNG or JPG up to 5MB.</div>
            </div>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Username</span>
            <div className="flex gap-2 mt-1">
              <input
                data-testid="settings-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="flex-1 bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-500"
              />
              <button onClick={saveUsername} disabled={saving} data-testid="settings-save" className="btn-liquid">
                <Save size={14} /> {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </label>

          <div className="grid md:grid-cols-2 gap-4 mt-5">
            <ReadOnly label="Email" value={user.email} />
            <ReadOnly label="Role" value={user.role} />
          </div>
        </section>

        <section className="glass-dark rounded-2xl p-6 md:p-8 mb-6" data-testid="settings-password-section">
          <h2 className="font-display font-bold text-xl mb-2">Change password</h2>
          <p className="text-sm text-zinc-400 mb-5">Updating your password signs you out on every other device.</p>
          <ChangePasswordForm idPrefix="settings-pw" onDone={() => toast.success("Password updated")} />
        </section>

        <section className="glass-red rounded-2xl p-6 md:p-8">
          <h2 className="font-display font-bold text-xl mb-2">Sign out</h2>
          <p className="text-sm text-zinc-400 mb-4">Signs you out of this device only.</p>
          <button
            onClick={logout}
            data-testid="settings-logout"
            className="btn-ghost-glass !px-5"
          >
            <LogOut size={14} /> Logout
          </button>
        </section>
      </div>
      <Footer discordUrl={DISCORD_URL} />
    </div>
  );
}

function ReadOnly({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className="mt-1 px-4 py-2.5 bg-black/30 border border-white/5 rounded-xl text-sm font-mono">{value}</div>
    </div>
  );
}
